import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  People,
  TrendingUp,
  SwapHoriz,
  PointOfSale,
} from '@mui/icons-material';
import AdminGuard from '../../components/common/AdminGuard';
import adminService from '../../services/adminService';
import { AdminDashboardStats } from '../../types';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
  background: theme.palette.mode === 'light' 
    ? 'rgba(255, 255, 255, 0.8)'
    : 'rgba(15, 23, 42, 0.8)',
  backdropFilter: 'blur(10px)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
}));

const StatCard = styled(StyledCard)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(3),
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.palette.mode === 'light'
      ? '0 12px 30px rgba(0, 0, 0, 0.1)'
      : '0 12px 30px rgba(0, 0, 0, 0.3)',
  },
}));

const StatValue = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  background: 'linear-gradient(135deg, #38B2AC 0%, #4FD1C7 100%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
}));

const AdminDashboard: React.FC = () => {
  const theme = useTheme();
  const [stats, setStats] = useState<AdminDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoading(true);
        const dashboardStats = await adminService.getDashboardStats();
        setStats(dashboardStats);
      } catch (err) {
        console.error('❌ Error loading dashboard stats:', err);
        setError('Failed to load admin statistics');
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!stats) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        No statistics available
      </Alert>
    );
  }

  const statsCards = [
    {
      label: 'Total Users',
      value: stats.users.totalUsers,
      icon: <People sx={{ fontSize: 40 }} />,
      color: 'primary.main',
    },
    {
      label: 'Active Users',
      value: stats.users.activeUsers,
      icon: <TrendingUp sx={{ fontSize: 40 }} />,
      color: 'success.main',
    },
    {
      label: 'Loyalty Programs',
      value: stats.programs.totalPrograms,
      icon: <PointOfSale sx={{ fontSize: 40 }} />,
      color: 'info.main',
    },
    {
      label: 'Total Transactions',
      value: stats.transactions.totalCount,
      icon: <SwapHoriz sx={{ fontSize: 40 }} />,
      color: 'warning.main',
    },
  ];

  return (
    <AdminGuard requiredPermission="canViewAnalytics">
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            📊 Admin Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            System overview and key metrics
          </Typography>
        </Box>

        {/* Key Stats */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 4 }}>
          {statsCards.map((stat, index) => (
            <StatCard key={index}>
              <CardContent>
                <Box sx={{ color: stat.color, mb: 2 }}>
                  {stat.icon}
                </Box>
                <StatValue>{stat.value.toLocaleString()}</StatValue>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {stat.label}
                </Typography>
              </CardContent>
            </StatCard>
          ))}
        </Box>

        {/* Detailed Analytics */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 3 }}>
          {/* New Users */}
          <StyledCard>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                New Users
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {stats.users.newUsersToday}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Today
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'info.main' }}>
                    {stats.users.newUsersThisWeek}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    This Week
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                    {stats.users.newUsersThisMonth}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    This Month
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </StyledCard>

          {/* Popular Programs */}
          <StyledCard>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                Popular Programs
              </Typography>
              {stats.programs.mostPopular.slice(0, 5).map((program, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">{program.name}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {program.usageCount}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      background: 'rgba(56, 178, 172, 0.1)',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        background: 'linear-gradient(90deg, #38B2AC 0%, #4FD1C7 100%)',
                        width: `${(program.usageCount / (stats.programs.mostPopular[0]?.usageCount || 1)) * 100}%`,
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </CardContent>
          </StyledCard>

          {/* Transaction Stats */}
          <StyledCard>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                Transaction Stats
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {stats.transactions.todayCount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Today
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'info.main' }}>
                    {(stats.transactions.totalPointsVolume / 1000000).toFixed(1)}M
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Points Tracked
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </StyledCard>
        </Box>
      </Box>
    </AdminGuard>
  );
};

export default AdminDashboard;